<template>
  <div class="odin-page">
    <AnchorMenu
      :links="anchorLinks"
      :respect-scroll-margin="true"
      @navigate="navigateTo"
    />
    <main id="odin-main" tabindex="-1" @click="followSectionLink">
      <section class="odin-hero" aria-labelledby="odin-title">
        <div class="container-wrapper hero-container">
          <div class="hero-heading">
            <p class="odin-eyebrow">
              <span class="accent-dot"></span> ODIN · AI SOFTWARE FOR SEO
            </p>
            <h1 id="odin-title">
              Your SEO assistant.<br /><span class="gradient-text"
                >In your browser.<br />On your desk.</span
              >
            </h1>
          </div>
          <figure class="hero-visual">
            <picture>
              <source
                srcset="
                  /odin/v3/hero-isolated-320.webp 320w,
                  /odin/v3/hero-isolated-640.webp 640w,
                  /odin/v3/hero-isolated-960.webp 960w
                "
                sizes="(max-width: 600px) 240px, (max-width: 992px) 320px, 400px"
                type="image/webp"
              />
              <img
                src="/odin/v3/hero-isolated.png"
                width="640"
                height="800"
                fetchpriority="high"
                alt="ARI ODIN concept: the complete graphite desktop robot, with one blue eye, metal-finish eyepatch, three-part beard, small arms and wheels."
              />
            </picture>
            <figcaption>CONCEPT RENDER · PLANNED PROTOTYPE</figcaption>
          </figure>
          <div class="hero-copy">
            <p class="hero-description">
              Bring research, content and review into your workflow with ODIN.
              Enquire about a tailored software implementation today. The
              optional robot companion is in development.
            </p>
            <div class="hero-ctas">
              <a class="green-btn odin-button" href="#pricing"
                >Discuss ODIN software <span aria-hidden="true">↗</span></a
              >
              <a class="odin-secondary-button" href="#preorder"
                >Pre-order the robot <span aria-hidden="true">↗</span></a
              >
            </div>
            <p class="hero-disclosure">
              Software scope, setup and access are agreed in your proposal.
              Robot pre-order requests require no payment.
            </p>
            <a class="text-link hero-demo-link" href="#workflows"
              >Explore ODIN’s demo <span aria-hidden="true">↓</span></a
            >
          </div>
        </div>
      </section>
      <div class="odin-intro-strip container-wrapper">
        <span
          ><i class="accent-dot"></i> Useful work. A little personality.</span
        ><span
          >Content <b>·</b> Visuals <b>·</b> Site structure <b>·</b> An optional
          desk companion</span
        ><span class="strip-tag">START WITH SOFTWARE ↓</span>
      </div>
      <section
        id="how-it-works"
        class="odin-section how-section container-wrapper"
      >
        <div class="section-heading">
          <div>
            <p class="odin-eyebrow">01 / IMPLEMENTED ODIN CAPABILITIES</p>
            <h2>Watch. Draft.<br />Review. Plan.</h2>
          </div>
          <p class="section-intro">
            Your ODIN setup is tailored to your workflow. We agree the included
            modules, integrations and access arrangements with you before
            implementation.
          </p>
        </div>
        <div class="steps odin-capabilities">
          <article>
            <span class="step-number">01 <span>↗</span></span>
            <h3>Monitor public sources.</h3>
            <p>
              Check selected public pages and RSS or Atom feeds. Retain source
              changes and send proposed updates for review.
            </p>
            <div class="capability-example">
              <small>EXAMPLE WORKFLOW</small
              ><span>Public feed → New item → Review queue</span>
            </div>
          </article>
          <article>
            <span class="step-number">02 <span>⑂</span></span>
            <h3>Draft and edit content.</h3>
            <p>
              Content Studio turns a brief and approved knowledge into drafts.
              Edit the wording, compare versions and review factual support.
            </p>
            <div class="capability-example">
              <small>EXAMPLE WORKFLOW</small
              ><span>Brief → Draft → Edit → Review</span>
            </div>
          </article>
          <article>
            <span class="step-number">03 <span>✓</span></span>
            <h3>Approve knowledge.</h3>
            <p>
              Review proposed facts and their evidence. Approve, reject or defer
              updates, with version and decision history retained.
            </p>
            <div class="capability-example">
              <small>EXAMPLE WORKFLOW</small
              ><span>Source evidence → Decision → History</span>
            </div>
          </article>
          <article>
            <span class="step-number">04 <span>◷</span></span>
            <h3>Schedule and plan.</h3>
            <p>
              Set source-check schedules, inspect job results and plan content
              in a calendar. Scheduling runs while the application worker is
              active.
            </p>
            <div class="capability-example">
              <small>EXAMPLE WORKFLOW</small
              ><span>Scheduled check → Finding → Content plan</span>
            </div>
          </article>
        </div>
        <p class="odin-capability-note">
          These modules are implemented in ODIN’s local-owner application and
          available for discussion in a tailored setup. Your proposal defines
          what you use directly and what ARI operates for you; it does not
          assume an existing hosted customer service.
        </p>
      </section>
      <OdinPlatform />
      <section
        class="odin-use-cases odin-section container-wrapper"
        aria-labelledby="odin-fit-title"
      >
        <div class="section-heading">
          <div>
            <p class="odin-eyebrow">A WORKFLOW THAT FITS YOUR BUSINESS</p>
            <h2 id="odin-fit-title">Your sites.<br />Your way of working.</h2>
          </div>
          <p class="section-intro">
            Alongside the agreed software modules, your proposal can include
            ARI-operated content, visuals, site structure, quality assurance and
            reporting.
          </p>
        </div>
        <div class="steps">
          <article>
            <span class="step-number">01 <span>↗</span></span>
            <h3>Existing domains.</h3>
            <p>
              Content, visuals, internal linking and page structure for domain
              portfolios and satellite sites.
            </p>
          </article>
          <article>
            <span class="step-number">02 <span>⑂</span></span>
            <h3>Monthly development.</h3>
            <p>
              Ongoing content architecture, rollout and review services, with
              the budget and delivery commitments agreed in your proposal.
            </p>
          </article>
          <article>
            <span class="step-number">03 <span>◉</span></span>
            <h3>A custom project.</h3>
            <p>
              A tailored workflow, model stack and quality framework for your
              niche, business model and scale.
            </p>
          </article>
        </div>
        <p class="odin-niche-note">
          <strong>Configured for your niche.</strong> iGaming, Betting, Sport,
          Finance, Crypto, SaaS, Health &amp; Wellness and Travel. Other niches
          are reviewed with your brief.
        </p>
      </section>
      <section class="connections-section odin-section container-wrapper">
        <div>
          <p class="odin-eyebrow">SOURCES TODAY. MORE CONNECTIONS AHEAD.</p>
          <h2>Know what changed.<br />Keep the source.</h2>
          <p>
            Public-page and feed monitoring is implemented. Changes become
            reviewable observations with source links and history; they are not
            automatically accepted as facts.
          </p>
          <p class="planned-note">
            <span>INTEGRATIONS BY SCOPE</span>Customer modules and access are
            agreed before implementation. The labels distinguish implemented
            source tools from planned account connectors.
          </p>
        </div>
        <div class="connection-list">
          <div v-for="(item, i) in connections" :key="item.name">
            <span class="connection-symbol">{{ item.icon }}</span>
            <div>
              <h3>{{ item.name }}</h3>
              <small class="connection-status">{{ item.status }}</small>
              <p>{{ item.detail }}</p>
            </div>
            <span class="connection-index">0{{ i + 1 }}</span>
          </div>
        </div>
      </section>
      <section id="workflows" class="workflow-section-band">
        <div class="odin-section workflow-section container-wrapper">
          <div class="section-heading">
            <div>
              <p class="odin-eyebrow">02 / INTERACTIVE WORKFLOW CONCEPT</p>
              <h2>Build the workflow.<br />Let ODIN follow it.</h2>
            </div>
            <p class="section-intro">
              Move a block, change a threshold and inspect the result in this
              browser demo. The drag-and-drop editor and analytics scenario
              illustrate a proposed experience, not a shipped ODIN workflow
              builder.
            </p>
          </div>
          <OdinWorkflowLoader ref="workflowLoader" />
          <div class="demo-footnote">
            <span>↑ Your changes affect this demo.</span>
            <p>
              Fictional sample data only. No account or robot is needed. The
              optional robot reactions illustrate an additional interaction; the
              demo does not operate a real device.
            </p>
          </div>
        </div>
      </section>
      <section class="conversation-section odin-section container-wrapper">
        <div class="conversation-product">
          <p class="odin-eyebrow">03 / LESS NOISE. MORE CONTEXT.</p>
          <h2>A useful update.<br />At the right moment.</h2>
          <p>
            ODIN’s monitoring records source changes and presents proposed
            updates for review. In-app notifications point you back to the work;
            a source observation still needs a decision.
          </p>
        </div>
        <div class="conversation-content">
          <div class="conversation-label">
            ILLUSTRATIVE UPDATE · FICTIONAL DATA
          </div>
          <div class="user-message">What needs my attention today?</div>
          <div class="robot-message">
            <span class="message-author"
              ><i class="accent-dot"></i> ODIN
              <small>Example response</small></span
            >
            <p>
              The public guide has changed. Review the updated passage and its
              source before adding it to your knowledge.
            </p>
            <div class="sample-page">
              example.com/guides <span>Sample finding</span>
            </div>
          </div>
          <div class="update-example">
            <span>SOURCE CHECK</span>
            <p>
              “The feed check finished. One new item is waiting for review.”
            </p>
          </div>
          <div class="update-example">
            <span>CONTENT REVIEW</span>
            <p>
              “The draft has a saved revision. Compare the wording and review
              its supporting evidence.”
            </p>
          </div>
          <div class="intended-controls">
            <span>IMPLEMENTED SCHEDULING CONTROLS</span>
            <p>
              Quiet hours <b>·</b> Pause <b>·</b> Job status <b>·</b> In-app
              notifications
            </p>
          </div>
        </div>
      </section>
      <section class="control-section odin-section container-wrapper">
        <div>
          <p class="odin-eyebrow">REVIEW, WITH A RECORD</p>
          <h2>You decide what<br />ODIN can do.</h2>
          <p>
            ODIN keeps review decisions distinct from generation. Saving a draft
            or importing a source does not approve it.
          </p>
        </div>
        <div class="control-list">
          <article>
            <span>01</span>
            <div>
              <h3>Keep evidence attached.</h3>
              <p>
                Public-source observations retain their source and version.
                Proposed knowledge remains separate from approved material.
              </p>
            </div>
          </article>
          <article>
            <span>02</span>
            <div>
              <h3>Approve what matters.</h3>
              <p>
                Review exact knowledge updates and draft versions. Decisions and
                editing history stay available for reference.
              </p>
            </div>
          </article>
          <article>
            <span>03</span>
            <div>
              <h3>See what ran.</h3>
              <p>
                Inspect scheduled jobs, pause work and cancel queued runs. These
                local-owner controls are distinct from planned customer role and
                connector-permission settings.
              </p>
            </div>
          </article>
        </div>
      </section>
      <OdinSoftware />
      <section class="presence-section">
        <div class="odin-section container-wrapper">
          <div class="section-heading">
            <div>
              <p class="odin-eyebrow">03 / YOUR OPTIONAL DESK COMPANION</p>
              <h2>Your workspace gains<br />a little presence.</h2>
            </div>
            <p class="section-intro">
              An expressive eye. A turn towards you.<br />Spoken updates when
              they’re welcome.<small
                >Physical behaviour and desktop mobility are planned prototype
                capabilities.</small
              >
            </p>
          </div>
          <div class="presence-composition">
            <div class="presence-images">
              <figure>
                <picture
                  ><source
                    srcset="
                      /odin/v2/workspace-720.webp  720w,
                      /odin/v2/workspace.webp     1440w
                    "
                    sizes="(max-width: 700px) calc(100vw - 30px), (max-width: 1200px) 59vw, 640px"
                    type="image/webp" />
                  <img
                    src="/odin/v2/workspace.jpg"
                    alt="ODIN concept beside a monitor, keyboard and notebook in a warm professional workspace."
                    width="1440"
                    height="960"
                    loading="lazy"
                /></picture>
                <figcaption>
                  A PLACE IN YOUR WORKDAY <span>CONCEPT RENDER</span>
                </figcaption>
              </figure>
            </div>
            <div class="architecture-note">
              <span>THE WORK CONTINUES IN SOFTWARE.</span>
              <p>
                ODIN software does not require a robot. The companion is
                intended to add spoken updates, expressions and movement at your
                desk. It is a separate hardware offer, not included in a
                software engagement.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section id="prototype" class="prototype-section-band">
        <div class="prototype-section odin-section container-wrapper">
          <figure class="prototype-image">
            <picture
              ><source
                srcset="
                  /odin/v3/hero-isolated-320.webp 320w,
                  /odin/v3/hero-isolated-640.webp 640w,
                  /odin/v3/hero-isolated-960.webp 960w
                "
                sizes="(max-width: 700px) 72vw, (max-width: 1200px) 34vw, 356px"
                type="image/webp" />
              <img
                src="/odin/v3/hero-isolated.png"
                width="640"
                height="800"
                loading="lazy"
                alt="Full-body ARI ODIN prototype concept with a textured graphite shell, one blue eye, silver-finish eyepatch and three-part beard, small arms and compact wheels."
            /></picture>
            <figcaption>
              3D-printed prototype concept · Design in development
            </figcaption>
          </figure>
          <div>
            <p class="odin-eyebrow">THE ROBOT / PROTOTYPE IN DEVELOPMENT</p>
            <h2>An early look at<br />what we’re building.</h2>
            <p>
              The ODIN robot is in development. These renders show the intended
              device, not a finished product. Its physical design and
              interaction features may change as the prototype develops.
            </p>
            <p class="prototype-construction">
              We plan to explore 3D-printed polymer panels, accessible fasteners
              and finished silver details. No physical prototype has been
              claimed as printed, assembled or tested.
            </p>
            <div class="prototype-facts">
              <span>Hardware price unconfirmed</span
              ><span>Hardware features unconfirmed</span
              ><span>No release date yet</span>
            </div>
          </div>
        </div>
      </section>
      <section
        class="odin-author odin-section container-wrapper"
        aria-labelledby="odin-author-title"
      >
        <img
          :src="authorPortrait"
          alt="Dominika, Head of Business Development &amp; AI Architecture"
          width="1024"
          height="1020"
          loading="lazy"
        />
        <div>
          <p class="odin-eyebrow">THE THINKING BEHIND THE PLATFORM</p>
          <h2 id="odin-author-title">Dominika</h2>
          <p class="odin-author-role">
            Head of Business Development &amp; AI Architecture
          </p>
          <blockquote>
            “The goal was to combine scalability, niche customisation and
            operational clarity in a way that creates real commercial value for
            clients.”
          </blockquote>
          <p class="odin-author-attribution">
            From the original platform profile, preserved as ODIN evolves.
          </p>
        </div>
      </section>
      <section id="faq" class="faq-section odin-section container-wrapper">
        <div>
          <p class="odin-eyebrow">A FEW THINGS TO KNOW</p>
          <h2>Good questions.<br />Honest answers.</h2>
          <a class="text-link" href="/contact">Talk to ARI <span>↗</span></a>
        </div>
        <div class="faq-list">
          <details v-for="item in faqs" :key="item.q">
            <summary>{{ item.q }}<span aria-hidden="true">+</span></summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </section>
      <section id="preorder" class="preorder-section-band">
        <div class="odin-final-software container-wrapper">
          <div>
            <p class="odin-eyebrow">START WITH THE SOFTWARE</p>
            <h2>Put ODIN to work.</h2>
            <p>
              Tell ARI what you need. We’ll agree the scope, pricing and setup
              before implementation. A robot request does not grant software
              access.
            </p>
          </div>
          <a class="green-btn odin-button" href="#pricing"
            >Discuss ODIN software <span aria-hidden="true">↗</span></a
          >
        </div>
        <div class="preorder-section odin-section container-wrapper">
          <div class="preorder-copy">
            <p class="odin-eyebrow">
              <span class="accent-dot"></span> OR ADD A COMPANION LATER
            </p>
            <h2>Bring ODIN<br />to your desk.</h2>
            <p>
              Register interest in the optional ODIN robot. We’ll contact you by
              email when hardware pricing, availability and next steps are
              ready. Software implementation is agreed separately; this request
              does not include software access.
            </p>
            <div class="preorder-note">
              <span>PROTOTYPE IN DEVELOPMENT</span>
              <p>
                A non-binding robot pre-order request.<br />No payment
                required.<br />Hardware features, pricing and availability are
                not yet confirmed.
              </p>
            </div>
            <a class="text-link" href="#workflows"
              >One more look at the workflow <span>↑</span></a
            >
          </div>
          <OdinRequest />
        </div>
      </section>
    </main>
  </div>
</template>
<script>
import AnchorMenu from "@/components/particles/AnchorMenu.vue";
import OdinWorkflowLoader from "./OdinWorkflowLoader.vue";
import OdinRequest from "./OdinRequest.vue";
import OdinPlatform from "./OdinPlatform.vue";
import OdinSoftware from "./OdinSoftware.vue";
import "./odin.css";
export default {
  name: "OdinPage",
  components: {
    AnchorMenu,
    OdinWorkflowLoader,
    OdinRequest,
    OdinPlatform,
    OdinSoftware,
  },
  methods: {
    followSectionLink(event) {
      const link = event.target.closest('a[href^="#"]');
      if (!link || !this.$el.contains(link)) return;
      event.preventDefault();
      this.navigateTo(link.getAttribute("href"));
    },
    async navigateTo(hash) {
      const target = this.$el.querySelector(hash);
      if (!target) return;
      const request = ++this.navigationVersion;
      const workflow = this.$el.querySelector("#workflows");
      if (
        target.getBoundingClientRect().top >=
        workflow.getBoundingClientRect().top
      ) {
        await this.$refs.workflowLoader.load();
        await this.$nextTick();
      }
      if (request !== this.navigationVersion) return;
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
      target.scrollIntoView({
        block: "start",
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
    },
  },
  metaInfo() {
    return {
      meta: [
        { vmid: "og:type", property: "og:type", content: "website" },
        {
          vmid: "og:image",
          property: "og:image",
          content: "https://www.ari.company/odin/v2/social.jpg",
        },
        { vmid: "og:image:width", property: "og:image:width", content: "1200" },
        {
          vmid: "og:image:height",
          property: "og:image:height",
          content: "630",
        },
        {
          vmid: "og:image:alt",
          property: "og:image:alt",
          content: "ARI ODIN concept robot. Prototype in development.",
        },
        {
          vmid: "twitter:card",
          name: "twitter:card",
          content: "summary_large_image",
        },
        ...(!["ari.company", "www.ari.company"].includes(
          window.location.hostname
        )
          ? [{ vmid: "robots", name: "robots", content: "noindex, nofollow" }]
          : []),
      ],
      script: [
        {
          type: "application/ld+json",
          json: {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "ODIN — SEO Platform & Optional Robot Companion",
            url: "https://www.ari.company/odin",
            description:
              "Tailored ODIN software engagements with individually agreed setup and access. Optional robot prototype available for a no-payment interest request. Interface concepts and workflow demo use sample data.",
          },
        },
      ],
    };
  },
  data() {
    return {
      navigationVersion: 0,
      authorPortrait: require("@/assets/img/ari-ai/dominika-b-ai.webp"),
      anchorLinks: [
        { id: 1, title: "Software", link: "#how-it-works" },
        { id: 2, title: "Demo", link: "#workflows" },
        { id: 3, title: "Pricing", link: "#pricing" },
        { id: 4, title: "Robot", link: "#prototype" },
        { id: 5, title: "FAQ", link: "#faq" },
        { id: 6, title: "Robot pre-order", link: "#preorder" },
      ],
      connections: [
        {
          icon: "↗",
          name: "Public pages & RSS / Atom",
          status: "IMPLEMENTED SOURCE TOOLS",
          detail:
            "Check public content and feeds, retain observations and review changes.",
        },
        {
          icon: "▤",
          name: "Manual source imports",
          status: "IMPLEMENTED SOURCE TOOLS",
          detail:
            "Import text, Markdown, JSON or CSV for review. Links in imports are not fetched automatically.",
        },
        {
          icon: "⌘",
          name: "CMS & publishing platforms",
          status: "PLANNED CONNECTORS",
          detail:
            "Connecting a CMS account or publishing site changes is not implemented in the supplied application.",
        },
        {
          icon: "✉",
          name: "Email & hosting accounts",
          status: "PLANNED CONNECTORS",
          detail:
            "Inbox access, infrastructure credentials and external alert delivery require separate integration work.",
        },
        {
          icon: "⌕",
          name: "Search Console & analytics",
          status: "PLANNED CONNECTORS",
          detail:
            "Traffic, rankings and account-based analytics in the concept screens are not live integrations.",
        },
        {
          icon: "☑",
          name: "External task tools & permissions",
          status: "PLANNED CONNECTORS / CONTROLS",
          detail:
            "Third-party task connections and customer role/connector permissions are not standard implemented modules.",
        },
      ],
      faqs: [
        {
          q: "Can I use ODIN without the robot?",
          a: "Yes. ODIN software does not require a physical robot. The existing application operates through a browser. The installation location and customer access method for your implementation are agreed in the proposal.",
        },
        {
          q: "Can I order the software now?",
          a: "You can enquire about ordering a tailored ODIN implementation now. ARI will discuss your requirements, scope, pricing and deployment arrangements before work begins. This is not an instant-access software subscription.",
        },
        {
          q: "How do I receive software access?",
          a: "Tell us what you need. We’ll agree the scope, pricing and setup with you before implementation. Your accepted proposal specifies the installation location, included modules, access method, delivery schedule and any prerequisites. Submitting an enquiry does not create an account or issue an invitation.",
        },
        {
          q: "What is included in each software engagement?",
          a: "Pilot+ is a starting point for validation, setup, quality review and a performance baseline. Launch focuses on managed production and reporting; Growth on larger programmes with dedicated oversight and scale operations. Custom is scoped individually. Your proposal specifies the software modules, setup and any ongoing ARI-operated services. Monthly page volumes are not guaranteed, and every module is not automatically included.",
        },
        {
          q: "How do pricing and payment work?",
          a: "Pilot+ starts from £2,900 per month, Launch from £4,900 and Growth from £14,900; Custom receives a tailored quote. Larger workloads and more complex requirements cost more. For Launch and Growth, scope, final pricing and payment terms are agreed in the individual proposal before work begins. Payment timing is agreed individually; this page takes no payment.",
        },
        {
          q: "Is the robot included?",
          a: "No. The robot is a separate optional hardware offer. A software enquiry does not order a robot, and a robot pre-order request does not grant or reserve software access.",
        },
        {
          q: "Is the robot available now?",
          a: "The robot is a prototype in development. Its final hardware features, price and delivery date are unconfirmed. The images are concept renders, not photographs of a finished device.",
        },
        {
          q: "Does robot pre-ordering require payment?",
          a: "No. It is a non-binding expression of interest. No payment is taken and no device, price or delivery date is reserved.",
        },
        {
          q: "Which software capabilities can we discuss?",
          a: "Public-page and feed monitoring, Content Studio drafting and editing, knowledge approval and history, and scheduling/calendar are implemented in ODIN’s local-owner application. Your proposal agrees the included modules and how you will access them. CMS, email, hosting and analytics-account connectors remain planned. The owner-specific Telegram integration is outside the standard offer.",
        },
        {
          q: "Is the drag-and-drop demonstration the current ODIN editor?",
          a: "No. It is an interactive concept on this page using fictional data. The supplied ODIN application uses forms and review screens for configured tasks; it has no general drag-and-drop workflow builder. The demo connects no accounts, accesses no websites and controls no robot.",
        },
        {
          q: "Can ARI help with content, visuals and site structure?",
          a: "Yes. The tailored proposal can include ARI-operated content production, visual support, site structure, quality assurance and reporting. These are scoped services, not a claim that every task is an automated customer-facing software feature.",
        },
      ],
    };
  },
};
</script>
